import { ErrorDisplay, Spinner } from '@automata/ui';
import { env, logger, useTabState } from '@automata/utils';
import { useFlag } from '@automata/utils/hooks';
import { useGetWorkflow, useSaveWorkflow } from '@automata/wfb-api';
import { useRouter } from 'next/router';
import { StrictMode, useCallback, useEffect, useRef } from 'react';
import { Provider, useStore } from 'react-redux';
import { useLocalStorage, useToggle } from 'usehooks-ts';

import { MainPanel } from './MainPanel';
import { MainToolbar } from './MainToolbar';
import { useWorkflowStore } from './useWorkflowStore';
import { getAutoSaveStorageKey } from '../../constants';
import { setWorkflowIsSaved } from '../../features/ui/uiSlice';
import { setWorkflowMetadata } from '../../features/workflow/workflowSlice';
import { selectWorkflowJsonToSave } from '../../features/workflowJsonSelectors';
import { useAppDispatch } from '../../hooks';
import { useAutoSaveWorkflow } from '../../hooks/useAutoSaveWorkflow';
import { useInitializeConfirmNavigateAway } from '../../hooks/useInitializeConfirmNavigateAway';
import { useInitializeScripts } from '../../hooks/useInitializeScripts';
import { useInitializeWorkcellAndDrivers } from '../../hooks/useInitializeWorkcellAndDrivers';
import { useInitializeWorkflow } from '../../hooks/useInitializeWorkflow';
import {
  type User,
  useInitializeWorkflowAuthor,
} from '../../hooks/useInitializeWorkflowAuthor';
import type { RootState } from '../../store';
import { TabId } from '../../types';
import { ErrorBoundary } from '../ErrorBoundary';
import IsPlanWorkflowModalOpenContextProvider from '../PreviewPanel/IsPlanWorkflowModalOpenContext/Provider';
import { SchedulerVersionModal } from '../SchedulerVersionModal';

export type Props = {
  workflowId?: string;
  user?: User;
};

export const CanvasAppInner = ({ workflowId, user }: Props): JSX.Element => {
  useInitializeConfirmNavigateAway();

  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const saveLockRef = useRef(false);
  const savedSnapshotRef = useRef<string | null>(null);

  const [activeTabId, setActiveTabId] = useTabState<TabId>('build', [
    'build',
    'preview',
  ]);

  const isAutoSaveFeatureEnabled = useFlag('fe.canvas_autosave');

  const storageKey = workflowId
    ? getAutoSaveStorageKey(workflowId)
    : 'temp-autosave-key';

  const [isAutoSaveEnabled, setIsAutoSaveEnabled] = useLocalStorage(
    storageKey,
    false
  );

  const handleClickReturnToWorkflow = useCallback(
    () => setActiveTabId('build'),
    [setActiveTabId]
  );

  const { getScriptsInfoError, getScriptsInfoIsLoading } =
    useInitializeScripts();

  const isExistingWorkflow = Boolean(workflowId);

  const {
    data: getWorkflowData,
    error: getWorkflowError,
    fetchStatus: getWorkflowFetchStatus,
  } = useGetWorkflow(
    { pathParams: { id: workflowId ?? '' } },
    { enabled: isExistingWorkflow }
  );

  const { getWorkcellIsLoading, getWorkcellError } =
    useInitializeWorkcellAndDrivers(getWorkflowData, isExistingWorkflow);

  const {
    schedulerVersionResolutionPrompt,
    isLoading: isLoadingInitialWorkflow,
    error: workflowInitializationError,
  } = useInitializeWorkflow(
    getWorkflowData,
    getWorkflowFetchStatus === 'fetching'
  );

  useInitializeWorkflowAuthor(user, isExistingWorkflow);

  const [
    isDescriptionPanelExpanded,
    toggleDescriptionPanelExpanded,
    setDescriptionPanelExpanded,
  ] = useToggle(false);

  const hideDescriptionPanel = useCallback(
    () => setDescriptionPanelExpanded(false),
    [setDescriptionPanelExpanded]
  );

  // Auto-save specific save handler
  const { saveWorkflow: autoSaveWorkflow, isIdle: autoSaveIsIdle } =
    useSaveWorkflow({
      onSuccess(data) {
        dispatch(setWorkflowMetadata(data));
        saveLockRef.current = false;

        // Only mark as saved if no new edits occurred since the snapshot
        const currentState = store.getState();
        const currentWorkflow = selectWorkflowJsonToSave(currentState);
        const currentSnapshot = JSON.stringify(currentWorkflow);

        if (currentSnapshot === savedSnapshotRef.current) {
          dispatch(setWorkflowIsSaved(true));
          logger.log('Auto-save completed successfully');
        } else {
          logger.log(
            'Auto-save completed, but new edits detected - keeping unsaved state'
          );
        }
        savedSnapshotRef.current = null;
      },
      onError(error) {
        logger.error('Auto-save failed:', error);
        saveLockRef.current = false;
        savedSnapshotRef.current = null;
        // Silent failure - don't show snackbar to user
      },
    });

  // Setup auto-save
  useAutoSaveWorkflow({
    workflowId,
    isEnabled:
      !!isAutoSaveFeatureEnabled &&
      isAutoSaveEnabled &&
      activeTabId === 'build' &&
      isExistingWorkflow,
    onAutoSave: useCallback(() => {
      // Check both manual lock and mutation status for safety
      if (saveLockRef.current || !autoSaveIsIdle) {
        logger.log('Auto-save skipped: save already in progress');
        return;
      }
      if (!workflowId) return; // Guard against undefined workflowId
      saveLockRef.current = true;
      // Get the latest workflow data from the store
      const currentState = store.getState();
      const workflowToSave = selectWorkflowJsonToSave(currentState);
      // Capture snapshot for comparison in onSuccess
      savedSnapshotRef.current = JSON.stringify(workflowToSave);
      autoSaveWorkflow(workflowToSave, workflowId);
    }, [autoSaveWorkflow, autoSaveIsIdle, workflowId, store]),
    saveLockRef,
  });

  if (getWorkcellError) {
    return (
      <ErrorDisplay
        error={getWorkcellError}
        userMessage="Failed to load workcell"
        logToConsole
      />
    );
  }

  if (getWorkflowError) {
    return (
      <ErrorDisplay
        error={getWorkflowError}
        userMessage="Failed to load workflow"
        logToConsole
      />
    );
  }

  if (getScriptsInfoError || workflowInitializationError) {
    return (
      <ErrorDisplay
        error={getScriptsInfoError || workflowInitializationError}
        userMessage="Failed to initialize workflow"
        logToConsole
      />
    );
  }

  if (
    getWorkflowFetchStatus === 'fetching' ||
    (getWorkcellIsLoading && !isExistingWorkflow) ||
    getScriptsInfoIsLoading ||
    isLoadingInitialWorkflow
  ) {
    return <Spinner />;
  }

  return (
    <>
      <SchedulerVersionModal
        currentVersion={schedulerVersionResolutionPrompt?.currentVersion}
        defaultSelectedVersion={
          schedulerVersionResolutionPrompt?.defaultSelectedVersion
        }
        onConfirm={schedulerVersionResolutionPrompt?.onConfirm}
      />
      <MainToolbar
        isExistingWorkflow={isExistingWorkflow}
        toggleDescriptionPanelExpanded={toggleDescriptionPanelExpanded}
        workflowId={workflowId}
        activeTabId={activeTabId}
        onChangeTab={setActiveTabId}
        saveLockRef={saveLockRef}
      />
      <MainPanel
        hideDescriptionPanel={hideDescriptionPanel}
        isDescriptionPanelExpanded={isDescriptionPanelExpanded}
        workflowId={workflowId}
        activeTabId={activeTabId}
        onClickReturnToWorkflow={handleClickReturnToWorkflow}
        isAutoSaveEnabled={isAutoSaveEnabled}
        setIsAutoSaveEnabled={setIsAutoSaveEnabled}
      />
    </>
  );
};

const CanvasApp = ({ workflowId, user }: Props) => {
  const router = useRouter();
  const store = useWorkflowStore(router);

  useEffect(() => {
    logger.log(`current version is ${env('NEXT_PUBLIC_VERSION')}`);
  }, []);

  return (
    <StrictMode>
      <ErrorBoundary>
        <IsPlanWorkflowModalOpenContextProvider>
          <Provider store={store}>
            <CanvasAppInner workflowId={workflowId} user={user} />
          </Provider>
        </IsPlanWorkflowModalOpenContextProvider>
      </ErrorBoundary>
    </StrictMode>
  );
};

export default CanvasApp;
